import { ComboBoxInputItemType } from "@ui-kit/form/ComboBox/ComboBox.props";
import { useCallback, useState } from "react";
import { type SearchItemProps } from "../SearchItem.web";

export default function useSearchItem<ItemType extends ComboBoxInputItemType>({
  handleInputValueChange,
  handleSelectedItemChange,
  onDropdownClickChange,
  onInputValueChange,
  items,
  selectedSet = [],
  ...rest
}: SearchItemProps<ItemType>) {
  const { multiple = false, single = false } = rest;

  if (multiple && !onDropdownClickChange) {
    throw new Error(
      "onDropdownClickChange must be provided when multiple is true"
    );
  }

  if (single && !handleInputValueChange) {
    throw new Error(
      "handleInputValueChange must be provided when single is true"
    );
  }

  const [inputValue, setInputValue] = useState<string>("");
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (single && handleInputValueChange) {
        handleInputValueChange(value);
      }
      if (onInputValueChange) {
        onInputValueChange(value);
      }
    },
    [single, handleInputValueChange, onInputValueChange]
  );

  const handleSingleSelectItem = useCallback(
    (item: ItemType | null) => {
      if (handleSelectedItemChange) {
        handleSelectedItemChange(item);
      }
    },
    [handleSelectedItemChange]
  );

  const handleDropdownClickChange = useCallback(
    (item: ItemType) => {
      if (onDropdownClickChange) {
        onDropdownClickChange(item);
      }
    },
    [onDropdownClickChange]
  );

  const handleRemoveItem = useCallback(
    (item: ItemType) => {
      if (handleSelectedItemChange) {
        handleSelectedItemChange(item);
      }
    },
    [handleSelectedItemChange]
  );

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
  }, []);

  const handleClearSearchTerm = useCallback(() => {
    setInputValue("");
    // In single-select mode, clearing the input means deselecting the item
    if (single && handleSelectedItemChange) {
      handleSelectedItemChange(null);
    }
  }, [single, handleSelectedItemChange]);

  return {
    handleSingleSelectItem,
    handleDropdownClickChange,
    handleRemoveItem,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleClearSearchTerm,
    isInputFocused,
    inputValue,
    items,
    selectedSet,
    ...rest,
  };
}
